import { InvalidInputError } from '../Errors/InvalidInputError.js';
import { forkProcess } from '../Process/Fork.js';
import { isArray, isFunction } from '../TypeChecking/index.js';
/**
 * ### ClassicResolver
 * Classe per realizzare una coda di esecuzione FIFO basata su array.
 *
 * Questa classe implementa il pattern definito.
 *
 * @note La classe è un riadattamento di ClassicResolver di ciaopeople-lwbu
 * @author Riccardo Di Dato (shatzu)
 * @author Lorenzo Muscariello (lwbu)
 *
 * @example
 * ```
 *          var myQueue = myQueue || [];
 *          myQueue.push(function(resolved) {
 *              console.log("function has been resolved with",resolved);
 *              //viene risolta solo dopo che sul resolver viene chiamato `resolve()`
 *          });
 *
 *          //.....
 *          myQueue = new ClassicResolver(myQueue);
 *
 *          myQueue.push(function(resolved) {
 *              //anche questa non viene risolta immediatamente
 *          });
 *
 *          myQueue.resolve({prop: 1});
 *
 *          myQueue.push(function(resolved) {
 *              //questa invece viene risolta immediatamente
 *          });
 * ```
 */
export class ClassicResolver {
    forkResolve;
    /**
     * Coda interna delle operazioni da eseguire.
     */
    internalQueue = [];
    /**
     * Se vero la coda è *attiva/risolta* e le operazioni vengono processate anche quando pushate
     */
    active = false;
    /**
     * Valore da usare per la risoluzione delle operazioni. Viene valorizzato viene *risolta* la coda per la prima volta.
     */
    resData = null;
    /**
     *
     * @param existingQueue Un array di funzioni con cui inizializzare la coda (default: array vuoto)
     * @param forkResolve Se true i processi eseguiti dalla coda saranno forkati in task dedicati (vedi {@link forkProcess})
     * @example
     * Possiamo inizializzare la classe con una coda di partenza
     * ```ts
     * const myQueue = new ClassicResolver([
     *      (res) => {
     *          console.log("Resolved", res);
     *      }
     * ]);
     * ```
     * Oppure senza parametri (coda vuota)
     * ```
     * const myQueue = new ClassicResolver();
     * ```
     */
    constructor(existingQueue, forkResolve = false) {
        this.forkResolve = forkResolve;
        if (existingQueue && isArray(existingQueue)) {
            // Utilizziamo this.push in modo da lanciare i controlli di tipo anche a runtime
            existingQueue.forEach(e => {
                this.push(e);
            });
        }
    }
    /**
     * Consente di risolvere la coda di chiamate.
     *
     * Tutti i callback in coda saranno chiamati in ordine FIFO con parametro **resolveItem**.
     *
     * Chiamare questo metodo rende la coda *attiva/risolta*, una coda attiva esegue i callback
     * direttamente in fase di push (in pratica se si pusha dopo un resolve non è necessario
     * chiamare di nuovo resolve)
     *
     * Chiamare {@link resolve} su una coda attiva non ha effetti (funziona solo la prima volta
     * che viene chiamato)
     *
     * @example
     *
     * ```ts
     *          let myQueue = myQueue || [];
     *          myQueue.push(function(resolved) {
     *              console.log("funzione1: ",resolved.name);
     *          });
     *
     *          //.....
     *          myQueue = new ClassicResolver<{name:string}>(myQueue);
     *
     *          myQueue.push(function(resolved) {
     *              console.log("funzione2: ",resolved.name);
     *          });
     *
     *          myQueue.resolve({name: "lorenzo"});
     * ```
     * Avrà come output
     * > funzione1: lorenzo
     *
     * > funzione2: lorenzo
     *
     * Se eseguiamo ulteriori push sulla coda saranno eseguite subito
     * utilizzando come valore per la callback {name: "lorenzo"}
     * ```
     *          myQueue.push(function(resolved) {
     *              console.log("funzione3: ",resolved.name);
     *          });
     * ```
     * Che darà output:
     * > funzione3: lorenzo
     *
     * @param resolveItem Valore di risoluzione, sarà passato alle funzioni in coda e memorizzato per i push successivi
     */
    resolve(resolveItem) {
        if (!this.active) {
            this.resData = resolveItem;
            let callback;
            while (this.internalQueue.length > 0) {
                callback = this.internalQueue.shift();
                const func = () => {
                    try {
                        callback(this.resData);
                    }
                    catch (e) {
                        console.error("There was an error executing waiter:", callback);
                    }
                };
                if (this.forkResolve) {
                    forkProcess(func);
                }
                else {
                    func();
                }
            }
            this.active = true;
        }
    }
    /**
     * Se la coda è attiva/risolta, la funzione passata in ingresso viene
     * immediatamente invocata col parametro di risoluzione.
     *
     * In caso contrario la funzione viene inserita nella coda di risoluzione.
     *
     * @param callback Funzione da inserire in coda
     * @throws {InvalidInputError} se callback non è una funzione
     */
    push(callback) {
        // Check a runtime perchè la classe può essere esposta via js
        if (!isFunction(callback)) {
            throw new InvalidInputError("ClassicResolver::push() expects functions as input");
        }
        if (this.active) {
            callback(this.resData);
        }
        else {
            this.internalQueue.push(callback);
        }
    }
}
